import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { PhotoRecordDto } from '@forest/contracts';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { RegionsCombobox } from '@/features/regions/RegionsCombobox';
import {
  useTreeSpecies,
  useWorkTypes,
  useAllPrimarii,
  useProjectTypes,
} from '@/features/regions/useRegions';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { useCreatePhoto, useUpdatePhoto } from './usePhotos';
import { useUiStore } from '@/stores/uiStore';
import { apiError } from '@/lib/api-client';
import { ParcelPicker } from './ParcelPicker';

/** The "free text allowed" project value that toggles the note field. */
const PROJECT_OTHER = 'Alte';
/** Default project for new records. */
const PROJECT_DEFAULT = 'PNERP';

type Props = {
  open: boolean;
  onClose: () => void;
  photo: PhotoRecordDto | null;
};

type FormShape = {
  fileName: string;
  latitude: string;
  longitude: string;
  downloadUrl: string;
  ucf: string;
  raion: string;
  primarie: string;
  ocolSilvic: string;
  entitateSilvica: string;
  anul: string;
  year: string;
  month: string;
  project: string;
  projectNote: string;
  codProiect: string;
  parcelId: string;
  speciaDeBaza: string;
  anulCultura: string;
  ultimaLucrare: string;
  codPrimarie: string;
};

const EMPTY: FormShape = {
  fileName: '',
  latitude: '',
  longitude: '',
  downloadUrl: '',
  ucf: '',
  raion: '',
  primarie: '',
  ocolSilvic: '',
  entitateSilvica: '',
  anul: '',
  year: '',
  month: '',
  project: PROJECT_DEFAULT,
  projectNote: '',
  codProiect: '',
  parcelId: '',
  speciaDeBaza: '',
  anulCultura: '',
  ultimaLucrare: '',
  codPrimarie: '',
};

function photoToForm(p: PhotoRecordDto): FormShape {
  return {
    fileName: p.fileName,
    latitude: String(p.latitude),
    longitude: String(p.longitude),
    downloadUrl: p.downloadUrl,
    ucf: p.ucf ?? '',
    raion: p.raion ?? '',
    primarie: p.primarie ?? '',
    ocolSilvic: p.ocolSilvic ?? '',
    entitateSilvica: p.entitateSilvica ?? '',
    anul: p.anul !== null ? String(p.anul) : '',
    year: p.year !== null ? String(p.year) : p.anul !== null ? String(p.anul) : '',
    month: p.month !== null ? String(p.month) : '',
    project: p.project ?? PROJECT_DEFAULT,
    projectNote: p.projectNote ?? '',
    codProiect: p.codProiect ?? '',
    parcelId: p.parcelId ?? '',
    speciaDeBaza: p.speciaDeBaza ?? '',
    anulCultura: p.anulCultura !== null ? String(p.anulCultura) : '',
    ultimaLucrare: p.ultimaLucrare ?? '',
    codPrimarie: p.codPrimarie ?? '',
  };
}

function formToCreateInput(values: FormShape) {
  const yr = values.year || values.anul;
  return {
    fileName: values.fileName.trim(),
    latitude: Number(values.latitude),
    longitude: Number(values.longitude),
    downloadUrl: values.downloadUrl.trim(),
    ...(values.ucf ? { ucf: values.ucf.trim() } : {}),
    ...(values.raion ? { raion: values.raion.trim() } : {}),
    ...(values.primarie ? { primarie: values.primarie.trim() } : {}),
    ...(values.ocolSilvic ? { ocolSilvic: values.ocolSilvic.trim() } : {}),
    ...(values.entitateSilvica ? { entitateSilvica: values.entitateSilvica.trim() } : {}),
    ...(yr ? { year: Number(yr), anul: Number(yr) } : {}),
    ...(values.month ? { month: Number(values.month) } : {}),
    project: values.project,
    ...(values.project === PROJECT_OTHER && values.projectNote
      ? { projectNote: values.projectNote.trim() }
      : {}),
    ...(values.codProiect ? { codProiect: values.codProiect.trim() } : {}),
    ...(values.parcelId ? { parcelId: values.parcelId } : {}),
    ...(values.speciaDeBaza ? { speciaDeBaza: values.speciaDeBaza.trim() } : {}),
    ...(values.anulCultura ? { anulCultura: Number(values.anulCultura) } : {}),
    ...(values.ultimaLucrare ? { ultimaLucrare: values.ultimaLucrare.trim() } : {}),
    ...(values.codPrimarie ? { codPrimarie: values.codPrimarie.trim() } : {}),
  };
}

function formToEditUpdateInput(values: FormShape) {
  const yr = values.year || values.anul;
  const body: Record<string, unknown> = {
    downloadUrl: values.downloadUrl.trim(),
    year: yr ? Number(yr) : null,
    anul: yr ? Number(yr) : null,
    month: values.month ? Number(values.month) : null,
    project: values.project,
    projectNote:
      values.project === PROJECT_OTHER && values.projectNote
        ? values.projectNote.trim()
        : null,
    parcelId: values.parcelId || null,
    speciaDeBaza: values.speciaDeBaza ? values.speciaDeBaza.trim() : null,
    anulCultura: values.anulCultura ? Number(values.anulCultura) : null,
    ultimaLucrare: values.ultimaLucrare ? values.ultimaLucrare.trim() : null,
    codPrimarie: values.codPrimarie ? values.codPrimarie.trim() : null,
  };
  // When parcelId set, clear denormalised metadata server-side by sending null
  if (values.parcelId) {
    body.ucf = null;
    body.raion = null;
    body.primarie = null;
    body.ocolSilvic = null;
    body.entitateSilvica = null;
    body.codProiect = null;
  }
  return body;
}

export function PhotoFormDialog({ open, onClose, photo }: Props) {
  const { t } = useTranslation(['photos', 'common']);
  const isEdit = photo !== null;
  const createMut = useCreatePhoto();
  const updateMut = useUpdatePhoto();
  const pushToast = useUiStore((s) => s.pushToast);
  const [parcelLabel, setParcelLabel] = useState<string | null>(null);

  const treeSpeciesQ = useTreeSpecies();
  const workTypesQ = useWorkTypes();
  const primariiQ = useAllPrimarii();
  const projectTypesQ = useProjectTypes();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<FormShape>({ defaultValues: EMPTY });

  const project = watch('project');
  const parcelId = watch('parcelId');

  useEffect(() => {
    if (open) {
      reset(photo ? photoToForm(photo) : EMPTY);
      // Prefer parcel label from joined object if present
      if (photo?.parcel) {
        setParcelLabel(
          `${photo.parcel.codProiect ?? '—'} · ${photo.parcel.ucf} · ${photo.parcel.raion}/${photo.parcel.primarie}`,
        );
      } else if (photo?.parcelLabel) {
        setParcelLabel(photo.parcelLabel);
      } else {
        setParcelLabel(null);
      }
    }
  }, [open, photo, reset]);

  const onSubmit = async (values: FormShape) => {
    const label = values.fileName;
    try {
      if (isEdit && photo) {
        const input = formToEditUpdateInput(values);
        await updateMut.mutateAsync({ id: photo.id, input });
        pushToast({
          variant: 'success',
          message: t('photos:form.updateSuccess', { label }),
        });
      } else {
        const input = formToCreateInput(values);
        await createMut.mutateAsync(input);
        pushToast({
          variant: 'success',
          message: t('photos:form.createSuccess', { label }),
        });
      }
      onClose();
    } catch (e) {
      const err = await apiError(e);
      pushToast({
        variant: 'error',
        message: t(
          isEdit ? 'photos:form.updateFailed' : 'photos:form.createFailed',
          { message: err.message },
        ),
      });
    }
  };

  const isPending = createMut.isPending || updateMut.isPending;

  // Project options come from the dynamic ProjectType reference table.
  // If the photo being edited carries a value that's no longer in the list
  // (e.g. a legacy 'ORANGE'/'WORLD_BANK' code), prepend it as a "(curent)"
  // bridge option so the select stays faithful to the stored value rather
  // than silently snapping to the first list entry. Mirrors the legacy-value
  // bridge in RegionsCombobox.
  const projectNames = projectTypesQ.data?.items.map((p) => p.name) ?? [];
  const projectOptions: { value: string; label: string }[] = projectNames.map(
    (name) => ({ value: name, label: name }),
  );
  if (project && !projectNames.includes(project)) {
    projectOptions.unshift({
      value: project,
      label: t('photos:form.field.projectLegacy', {
        value: project,
        defaultValue: '{{value}} (curent)',
      }),
    });
  }

  // Year / month / project / projectNote controls (shared between create & edit)
  const yearMonthProject = (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <FormField label={t('photos:form.field.year', { defaultValue: 'An' })} htmlFor="ph-year">
          <Input
            id="ph-year"
            type="number"
            min={1900}
            max={2200}
            {...register('year')}
          />
        </FormField>
        <FormField label={t('photos:form.field.month', { defaultValue: 'Lună' })} htmlFor="ph-month">
          <Select id="ph-month" {...register('month')}>
            <option value="">—</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {String(m).padStart(2, '0')}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label={t('photos:form.field.project', { defaultValue: 'Proiect' })} htmlFor="ph-project">
          <Select id="ph-project" {...register('project')}>
            {projectOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormField>
      </div>
      {project === PROJECT_OTHER ? (
        <FormField
          label={t('photos:form.field.projectNote', { defaultValue: 'Notă proiect' })}
          htmlFor="ph-project-note"
        >
          <Input id="ph-project-note" {...register('projectNote')} />
        </FormField>
      ) : null}
    </>
  );

  // Silviculture reference fields (shared between create & edit). Optional on
  // the web surface — required-ness is enforced on the mobile capture flow.
  const treeSpeciesOptions = treeSpeciesQ.data?.items ?? [];
  const workTypeOptions = [...(workTypesQ.data?.items ?? [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  );
  const primarieOptions = primariiQ.data?.items ?? [];
  const culturaYears = Array.from(
    { length: new Date().getFullYear() - 2015 + 1 },
    (_, i) => 2015 + i,
  );

  const silvicultureFields = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <FormField
        label={t('photos:form.field.speciaDeBaza', { defaultValue: 'Specia de Bază (SB)' })}
        htmlFor="ph-sb"
      >
        <Select id="ph-sb" {...register('speciaDeBaza')}>
          <option value="">—</option>
          {treeSpeciesOptions.map((s) => (
            <option key={s.id} value={s.cod}>
              {s.name ? `${s.cod} — ${s.name}` : s.cod}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField
        label={t('photos:form.field.anulCultura', {
          defaultValue: 'Anul creării culturii silvice',
        })}
        htmlFor="ph-anul-cultura"
      >
        <Select id="ph-anul-cultura" {...register('anulCultura')}>
          <option value="">—</option>
          {culturaYears.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField
        label={t('photos:form.field.ultimaLucrare', {
          defaultValue: 'Ultima lucrare executată',
        })}
        htmlFor="ph-ultima-lucrare"
      >
        <Select id="ph-ultima-lucrare" {...register('ultimaLucrare')}>
          <option value="">—</option>
          {workTypeOptions.map((w) => (
            <option key={w.id} value={w.name}>
              {w.name}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField
        label={t('photos:form.field.codPrimarie', { defaultValue: 'Primarii_Raion_Cod' })}
        htmlFor="ph-cod-primarie"
      >
        <Select id="ph-cod-primarie" {...register('codPrimarie')}>
          <option value="">—</option>
          {primarieOptions.map((p) => (
            <option key={p.id} value={p.cod}>
              {p.name} — {p.raion} · {p.cod}
            </option>
          ))}
        </Select>
      </FormField>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isPending) onClose();
      }}
      title={isEdit ? t('photos:form.editTitle') : t('photos:form.createTitle')}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3" noValidate>
        <FormField
          label={t('photos:form.field.fileName')}
          htmlFor="ph-fn"
          error={errors.fileName?.message}
          required
        >
          <Input
            id="ph-fn"
            {...register('fileName', { required: 'Required' })}
            disabled={isEdit}
          />
        </FormField>

        {isEdit ? (
          <>
            {/* Edit mode: parcel picker + year/month + project + downloadUrl */}
            <FormField
              label={t('photos:form.field.parcel', { defaultValue: 'Poligon' })}
              htmlFor="ph-parcel"
              hint={t('photos:form.hint.parcelPicker', {
                defaultValue:
                  'Selectează poligonul pentru a completa automat metadatele (GPS etc.).',
              })}
            >
              <ParcelPicker
                value={parcelId || null}
                label={parcelLabel}
                onPick={({ id, label, latitude, longitude }) => {
                  setValue('parcelId', id, { shouldDirty: true });
                  setParcelLabel(label);
                  const latDirty = dirtyFields.latitude;
                  const lonDirty = dirtyFields.longitude;
                  if (
                    Number.isFinite(latitude) &&
                    Number.isFinite(longitude) &&
                    !latDirty &&
                    !lonDirty
                  ) {
                    setValue('latitude', String(latitude), { shouldDirty: true });
                    setValue('longitude', String(longitude), { shouldDirty: true });
                  }
                }}
                onClear={() => {
                  setValue('parcelId', '', { shouldDirty: true });
                  setParcelLabel(null);
                }}
              />
            </FormField>

            {yearMonthProject}

            {silvicultureFields}

            <FormField
              label={t('photos:form.field.downloadUrl')}
              htmlFor="ph-url"
              error={errors.downloadUrl?.message}
              required
            >
              <Input
                id="ph-url"
                type="url"
                {...register('downloadUrl', { required: 'Required' })}
              />
            </FormField>
          </>
        ) : (
          <>
            {/* Create mode: manual GPS + denormalised fields */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField
                label={t('photos:form.field.latitude')}
                htmlFor="ph-lat"
                error={errors.latitude?.message}
                required
              >
                <Input
                  id="ph-lat"
                  type="number"
                  step="any"
                  inputMode="decimal"
                  {...register('latitude', {
                    required: 'Required',
                    validate: (v) => {
                      const n = Number(v);
                      if (isNaN(n) || n < -90 || n > 90) return 'Must be between -90 and 90';
                      return true;
                    },
                  })}
                />
              </FormField>
              <FormField
                label={t('photos:form.field.longitude')}
                htmlFor="ph-lon"
                error={errors.longitude?.message}
                required
              >
                <Input
                  id="ph-lon"
                  type="number"
                  step="any"
                  inputMode="decimal"
                  {...register('longitude', {
                    required: 'Required',
                    validate: (v) => {
                      const n = Number(v);
                      if (isNaN(n) || n < -180 || n > 180) return 'Must be between -180 and 180';
                      return true;
                    },
                  })}
                />
              </FormField>
            </div>

            <FormField
              label={t('photos:form.field.downloadUrl')}
              htmlFor="ph-url"
              error={errors.downloadUrl?.message}
              required
            >
              <Input
                id="ph-url"
                type="url"
                {...register('downloadUrl', { required: 'Required' })}
              />
            </FormField>

            {yearMonthProject}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label={t('photos:form.field.ucf')} htmlFor="ph-ucf">
                <Input id="ph-ucf" {...register('ucf')} />
              </FormField>
              {/* Create branch only (photo is always null here) — the
                  field's initial value comes from RHF's EMPTY defaults, so
                  defaultValue stays empty. */}
              <FormField label={t('photos:form.field.raion')} htmlFor="ph-raion">
                <RegionsCombobox
                  kind="raion"
                  id="ph-raion"
                  defaultValue=""
                  {...register('raion')}
                />
              </FormField>
              <FormField label={t('photos:form.field.primarie')} htmlFor="ph-prim">
                <RegionsCombobox
                  kind="primarie"
                  id="ph-prim"
                  defaultValue=""
                  {...register('primarie')}
                />
              </FormField>
              <FormField label={t('photos:form.field.ocolSilvic')} htmlFor="ph-ocol">
                <RegionsCombobox
                  kind="ocolSilvic"
                  id="ph-ocol"
                  defaultValue=""
                  {...register('ocolSilvic')}
                />
              </FormField>
              <FormField label={t('photos:form.field.entitateSilvica')} htmlFor="ph-ent">
                <RegionsCombobox
                  kind="entitateSilvica"
                  id="ph-ent"
                  defaultValue=""
                  {...register('entitateSilvica')}
                />
              </FormField>
              <FormField label={t('photos:form.field.codProiect')} htmlFor="ph-cod">
                <Input id="ph-cod" {...register('codProiect')} />
              </FormField>
            </div>

            {silvicultureFields}
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isPending}
          >
            {t('common:action.cancel')}
          </Button>
          <Button type="submit" loading={isPending}>
            {isEdit ? t('photos:form.saveEdit') : t('photos:form.saveCreate')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
